/**
 * Pure parts of the return dialog (P4-e): the fault changes staged against the car's stored damage map, and the
 * request body. The map is edited LOCALLY (a working copy) and only the difference travels with the protocol — the
 * server applies it in the same transaction, so a refused return leaves the real map untouched.
 */

import type { Dil, VraceniVstup, Zavada, ZavadaBody } from './api';
import { kauceZFormulare, kontrolniSeznamZFormulare, type PredaniForm } from './predani-helpers';

type ZavadaZmena = VraceniVstup['zavady'][number];

/** The five ticks of the checklist, worded for the return. The keys are the handover's. */
export const KONTROLNI_BODY_VRACENI = [
  { key: 'klic', label: 'Klíče vráceny' },
  { key: 'maly_tp', label: 'Malý technický průkaz vrácen' },
  { key: 'zelena_karta', label: 'Zelená karta vrácena' },
  { key: 'najemni_smlouva', label: 'Nájemní smlouva vrácena' },
  { key: 'stav_potvrzen', label: 'Zákazník potvrdil stav vozu' },
] as const;

/** Stage a fault (new or changed) in the working copy of the damage map. `token` makes the entry's key unique per staging. */
export function stageZavadu(
  working: Zavada[],
  parts: readonly Dil[],
  dil: number,
  body: ZavadaBody,
  token: number,
): Zavada[] {
  const part = parts.find((p) => p.id_dilu === dil);
  const entry: Zavada = {
    id_dilu: dil,
    nazev_dilu: part?.nazev ?? `Díl ${dil}`,
    zona: part?.zona ?? 'exterior',
    zavaznost: body.zavaznost,
    popis: body.popis,
    zmeneno_at: `staged-${token}`,
    nalezeno_at: `staged-${token}`,
  };
  const rest = working.filter((z) => z.id_dilu !== dil);
  return [...rest, entry].sort((a, b) => a.id_dilu - b.id_dilu);
}

/** Take a fault staged in this dialog out of the working copy again (given up). A fault the car already had cannot be removed here. */
export const unstageZavadu = (working: Zavada[], dil: number): Zavada[] =>
  working.filter((z) => z.id_dilu !== dil);

/**
 * Undo ONE staged change: the part goes back to what the car's stored map says (its fault restored, or the fault staged here dropped).
 */
export function vratZmenu(stored: readonly Zavada[], working: readonly Zavada[], dil: number): Zavada[] {
  const was = stored.find((z) => z.id_dilu === dil);
  const rest = working.filter((z) => z.id_dilu !== dil);
  return (was ? [...rest, was] : rest).sort((a, b) => a.id_dilu - b.id_dilu);
}

export type DruhZmeny = 'nova' | 'zmenena';

export interface ZmenaZavady {
  id_dilu: number;
  druh: DruhZmeny;
  /** The fault as it will be. */
  nove: Zavada;
  /** The fault as it was (null for a new one). */
  puvodni: Zavada | null;
}

/**
 * The difference between the stored map and the working copy, by part number: a fault found or changed. A part set back to
 * what it was is no change. A stored fault is never missing from the working copy (nothing is repaired during a rental).
 */
export function zmenyZavad(stored: readonly Zavada[], working: readonly Zavada[]): ZmenaZavady[] {
  const before = new Map(stored.map((z) => [z.id_dilu, z]));
  const after = new Map(working.map((z) => [z.id_dilu, z]));
  const out: ZmenaZavady[] = [];
  for (const [id, now] of after) {
    const was = before.get(id) ?? null;
    if (!was) out.push({ id_dilu: id, druh: 'nova', nove: now, puvodni: null });
    else if (was.zavaznost !== now.zavaznost || was.popis.trim() !== now.popis.trim())
      out.push({ id_dilu: id, druh: 'zmenena', nove: now, puvodni: was });
  }
  return out.sort((a, b) => a.id_dilu - b.id_dilu);
}

/** The fault changes as the API wants them: severity + text of each fault found or changed. */
export const zavadyBody = (zmeny: readonly ZmenaZavady[]): ZavadaZmena[] =>
  zmeny.map((z) => ({ id_dilu: z.id_dilu, zavaznost: z.nove.zavaznost, popis: z.nove.popis.trim() }));

/**
 * The request body of a return. A technical block sends km, the note and its fault changes; the checklist and the
 * deposit (a fact: returned or not) belong to a customer rental.
 */
export function vraceniBody(
  f: PredaniForm,
  opts: { technicka: boolean; zmeny: readonly ZmenaZavady[] },
): VraceniVstup {
  const base: VraceniVstup = {
    km: Number(f.km),
    poznamka: f.poznamka.trim(),
    zavady: zavadyBody(opts.zmeny),
  };
  if (opts.technicka) return base;
  return {
    ...base,
    kontrolni_seznam: kontrolniSeznamZFormulare(f),
    kauce: kauceZFormulare(f.kauce),
  };
}

/** `95` → `1 h 35 min`, `2 900` → `2 dny 0 h 20 min`: how late a car came back, for the confirmation toast. */
export function zpozdeniText(min: number): string {
  const dny = Math.floor(min / 1440);
  const hod = Math.floor((min % 1440) / 60);
  const m = min % 60;
  const cast = [dny > 0 ? `${dny} ${dny === 1 ? 'den' : dny < 5 ? 'dny' : 'dní'}` : '', dny > 0 || hod > 0 ? `${hod} h` : '', `${m} min`];
  return cast.filter(Boolean).join(' ');
}
